import { FormGroup, FormParagraph, FormSection } from '@ksyos/design-system';
import type { TreatmentData } from '@common/validation/customControls/osas/osasAllTreatmentsSchemas/types';
import type { TreatmentOptionKey } from '@common/validation/customControls/osas/osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import type {
    OsasTreatmentBreakData,
    OsasTreatmentData,
} from '@common/validation/customControls/osas/osasTreatmentChoice/types';
import type { ValidationErrors } from '@frontend/utils';
import { treatmentOptionsLabels } from '@common/validation/customControls/osas/osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import { CheckboxGroupOld } from '@frontend/components/fields/CheckBoxGroup';
import { TextAreaFormGroup } from '../../../processActivityFormGroupFields';

type TreatmentToBreakFormSectionProps = {
    data: OsasTreatmentData & OsasTreatmentBreakData;
    errors: ValidationErrors;
    inProgressTreatments?: TreatmentData;
    onBreakTreatments: (treatmentsToBreak: Array<keyof TreatmentData>) => void;
    onChange: (
        data: Partial<OsasTreatmentData & OsasTreatmentBreakData>,
        validateKeys?: string | string[] | undefined
    ) => void;
};

export function TreatmentToBreakFormSection(props: Readonly<TreatmentToBreakFormSectionProps>) {
    const { data, errors, inProgressTreatments, onBreakTreatments, onChange } = props;

    if (!inProgressTreatments) {
        return null;
    }

    const currentTreatmentsInArray = Object.keys(inProgressTreatments).filter(
        (key) => inProgressTreatments[key as keyof TreatmentData]
    ) as (keyof TreatmentData)[];

    return (
        <FormSection title="Huidig beleid">
            <FormGroup label="Beleid">
                {currentTreatmentsInArray.map((key) => (
                    <FormParagraph key={key}>{treatmentOptionsLabels[key as TreatmentOptionKey]}</FormParagraph>
                ))}
            </FormGroup>

            <CheckboxGroupOld
                checkboxOptions={currentTreatmentsInArray.map((key) => ({
                    checked: data.treatmentToBreak.includes(key),
                    label: treatmentOptionsLabels[key as TreatmentOptionKey],
                    onChange: async () => {
                        const updatedTreatmentToBreak = data.treatmentToBreak.includes(key)
                            ? data.treatmentToBreak.filter((t) => t !== key)
                            : [...data.treatmentToBreak, key];

                        onChange({ treatmentToBreak: updatedTreatmentToBreak }, 'treatmentToBreak');
                        onBreakTreatments(updatedTreatmentToBreak);
                    },
                }))}
                error={errors.get('treatmentToBreak')}
                hasErrors={errors.has('treatmentToBreak')}
                label="Afbreken; therapie beëindigen"
            />

            <TextAreaFormGroup
                fieldKey="treatmentToBreakRemark"
                isRequired={data.treatmentToBreak.length > 0}
                label="Toelichting"
            />
        </FormSection>
    );
}
